import type { Map as ImmutableMap } from 'immutable';

import type RootState from 'app-components/utils/redux-store/rootState';

import store from '../../utils/redux-store';
import { type FEATURE_FLAGS_REDUCER_KEY, featureFlagsSelectors } from '../feature-flags';
import { isErrorType } from '../institution-logins/institutionLoginsUtils';
import {
  ACCOUNTS_REDUCER_KEY,
  type Account,
  type AccountClassification,
  type AccountProps,
  type AccountType,
  type AccountTypeNodeProps,
} from './accountsTypes';

type DeletedAccountCandidate = Pick<AccountProps, 'id' | 'isDeleted'>;

/*
 * Returns the ids for accounts that should cascade-delete dependent data.
 */
export function getDeletedAccountIds(accounts: ReadonlyArray<DeletedAccountCandidate>) {
  return accounts.reduce<string[]>((deletedIds, account) => {
    if (account.isDeleted && account.id) {
      deletedIds.push(account.id);
    }
    return deletedIds;
  }, []);
}

// non-imported account lookup to prevent circular imports
const getAccountById = (state: Pick<RootState, ACCOUNTS_REDUCER_KEY> | undefined | null, id?: string | null) => {
  if (!id) return undefined;
  const lookupState = state || store?.getState();
  return lookupState?.[ACCOUNTS_REDUCER_KEY]?.resourcesById?.get(id);
};

// Retriever import (NOT SELECTORS)
export function getAccountString(id?: string | null, state?: Pick<RootState, ACCOUNTS_REDUCER_KEY> | null) {
  const account = getAccountById(state, id);
  if (account) {
    return account.name;
  }
  return '[Account Not Synced]';
}
export function getCurrencyString(id?: string | null, state?: Pick<RootState, ACCOUNTS_REDUCER_KEY> | null) {
  const account = getAccountById(state, id);
  if (account) {
    return account.currency;
  }
  return 'N/A';
}

// -------------------------------------------------------
// isLoanAccountFromId
//
export function isLoanAccountFromId(
  id: string | undefined | null,
  state?: Pick<RootState, ACCOUNTS_REDUCER_KEY> | null,
) {
  return isLoanAccount(getAccountById(state, id));
}

// -------------------------------------------------------
// isInvestmentAccountFromId
//
export function isInvestmentAccountFromId(
  id: string | undefined | null,
  state?: Pick<RootState, ACCOUNTS_REDUCER_KEY> | null,
) {
  return isInvestmentAccount(getAccountById(state, id));
}

// -------------------------------------------------------
// isActiveConnectedAccount
//
export function isActiveConnectedAccountFromId(
  id: string | undefined | null,
  state?: Pick<RootState, ACCOUNTS_REDUCER_KEY> | null,
) {
  return isAccountConnected(getAccountById(state, id));
}

// -------------------------------------------------------
// isLoanAccount
//
export function isLoanAccount(acct?: Pick<AccountProps, 'type'>) {
  return Boolean(acct && acct.type === 'LOAN');
}

// -------------------------------------------------------
// isInvestmentAccount
//
export function isInvestmentAccount(acct?: AccountProps) {
  return Boolean(acct && acct.type === 'INVESTMENT');
}

export function isStripeAccount(acct?: Pick<AccountProps, 'aggregators'>) {
  const channel = acct?.aggregators?.get(0)?.channel;
  return Boolean(channel === 'STRIPE_TEST' || channel === 'STRIPE');
}

// -------------------------------------------------------
// isAccountConnected
//
export const isAccountConnected = (account?: Pick<AccountProps, 'isConnected'>) => account?.isConnected;

// A disconnected account will be defined as an account which belongs to an institution-login but is not connected.
// If it doesn't belong to an institution-login then it will be considered manual.
//
// Note: If you want to see whether an account is connected or not-connected regardless of whether it is considered
//       manual, use isAccountConnected(account) or !isAccountConnected(account).
//         !isAccountConnected(account) = isAccountDisconnected(account) || isAccountManual(account)
//
export const isAccountDisconnected = (account?: AccountProps, _institutionLogin?: unknown) =>
  account?.institutionLoginId && account.institutionLoginId !== '0' && !account.isConnected;

// A manual account will be defined as an account which belongs to no institution-login. If it belongs to an
// institution-login but is not connected, we will consider it disconnected account, not manual.
//
// Note: If you want to see whether an account is connected or not-connected regardless of whether it is considered
//       manual, use isAccountConnected(account) or !isAccountConnected(account).
//         !isAccountConnected(account) = isAccountDisconnected(account) || isAccountManual(account)
//
export const isAccountManual = (account?: Pick<AccountProps, 'institutionLoginId'>): boolean =>
  !account?.institutionLoginId || account.institutionLoginId === '0';

export function transactionsNotSupportedForAccountId(
  accountId: string | undefined | null,
  hideConnectedLoanTransactions: boolean = false,
  state: Pick<RootState, typeof FEATURE_FLAGS_REDUCER_KEY | ACCOUNTS_REDUCER_KEY>,
) {
  const investmentTransactionsEnabled = featureFlagsSelectors.getFeatureFlag(state, 'investmentTransactions');
  if (!investmentTransactionsEnabled && isInvestmentAccountFromId(accountId, state)) return true;

  return Boolean(
    isLoanAccountFromId(accountId, state) &&
      hideConnectedLoanTransactions &&
      isActiveConnectedAccountFromId(accountId, state),
  );
}

export type BalanceLabelMode =
  | 'NO_ACCOUNT_NODE'
  | 'NO_ACCOUNT'
  | 'NONE'
  | 'TYPE_HEADER_TOTAL_BALANCE'
  | 'CLOSED_BALANCE_AS_OF'
  | 'MANUAL_BALANCE_AS_OF'
  | 'MANUAL_ASSET_VALUE_AS_OF'
  | 'ZILLOW_KBB_VALUE_AS_OF'
  | 'APPLE_CASH_TOTAL_BALANCE_TOOLTIP'
  | 'BANK_BALANCE_WITH_PENDING_EDIT'
  | 'STANDARD_BALANCE_WITH_PENDING'
  | 'STANDARD_BANK_BALANCE'
  | 'CONNECTED_ASSET_VALUE_AS_OF'
  | 'CONNECTED_ASSET_VALUE';

export type BalanceLabelModeInput = {
  hasAccountNode: boolean;
  isLeaf: boolean;
  hasAccount: boolean;
  isAccountOpen: boolean;
  isManualRefreshEnabled: boolean;
  isManualAccount: boolean;
  isAssetAccount: boolean;
  isZillowOrKbb: boolean;
  isAppleCash: boolean;
  isConnected: boolean;
  isBankAccount: boolean;
  isCreditAccount: boolean;
  isLoanAccount: boolean;
  includePending: boolean;
  hasBalanceAsOfLabel: boolean;
};

export const getBalanceLabelMode = ({
  hasAccountNode,
  isLeaf,
  hasAccount,
  isAccountOpen,
  isManualRefreshEnabled,
  isManualAccount,
  isAssetAccount,
  isZillowOrKbb,
  isAppleCash,
  isConnected,
  isBankAccount,
  isCreditAccount,
  isLoanAccount,
  includePending,
  hasBalanceAsOfLabel,
}: BalanceLabelModeInput): BalanceLabelMode => {
  if (!hasAccountNode) {
    // No account node available; nothing to label.
    return 'NO_ACCOUNT_NODE';
  }

  if (!isLeaf) {
    // Non-leaf (type/header) node should show the aggregate total balance label.
    return 'TYPE_HEADER_TOTAL_BALANCE';
  }

  if (!hasAccount) {
    // Leaf node without a concrete account record.
    return 'NO_ACCOUNT';
  }

  if (!isAccountOpen) {
    // Closed account should show balance as of closed date.
    return 'CLOSED_BALANCE_AS_OF';
  }

  if (isManualRefreshEnabled && isManualAccount) {
    // Manual accounts use "Balance/Value as of ..." with tooltip refresh.
    return isAssetAccount ? 'MANUAL_ASSET_VALUE_AS_OF' : 'MANUAL_BALANCE_AS_OF';
  }

  if (isManualRefreshEnabled && isZillowOrKbb) {
    // Zillow/KBB connected assets use "Value as of ..." with refresh tooltip.
    return 'ZILLOW_KBB_VALUE_AS_OF';
  }

  if (isAppleCash) {
    // Apple Cash shows "Total balance" with Apple Wallet tooltip.
    return 'APPLE_CASH_TOTAL_BALANCE_TOOLTIP';
  }

  if (isConnected && (isBankAccount || isCreditAccount || isLoanAccount)) {
    if (includePending) {
      // Bank accounts with pending included show editable pending tooltip.
      return isBankAccount ? 'BANK_BALANCE_WITH_PENDING_EDIT' : 'STANDARD_BALANCE_WITH_PENDING';
    }
    // Connected bank/credit/loan without pending included.
    return 'STANDARD_BANK_BALANCE';
  }

  if (isConnected && isAssetAccount) {
    // Connected assets show value, optionally "as of" if date is present.
    return hasBalanceAsOfLabel ? 'CONNECTED_ASSET_VALUE_AS_OF' : 'CONNECTED_ASSET_VALUE';
  }

  // Fallback when none of the supported cases match.
  return 'NONE';
};

export const isAccountNodeSelectable = (
  node: AccountTypeNodeProps,
  initialNode: AccountTypeNodeProps | undefined,
  businessEnabled: boolean,
  mode: 'add' | 'edit' = 'add', // default to add
) => {
  if (!node.selectable || (!businessEnabled && node.business)) {
    return false;
  }

  if (mode === 'add') {
    // ADD LOGIC - Do not restrict the user from changing account types when adding a new manual account
    return (
      !initialNode ||
      initialNode.qcsType === 'UNKNOWN' ||
      (initialNode.qcsType === 'INVESTMENT' ? node.qcsType === 'INVESTMENT' : node.qcsType !== 'INVESTMENT') ||
      (initialNode.qcsType === 'PAYMENT' ? node.qcsType === 'PAYMENT' : node.qcsType !== 'PAYMENT')
    );
  } else {
    // EDIT LOGIC
    // If initial-node is set and not unknown, then here is where we can lock specific types. For example,
    // if account is initially set to type investment, then this can be used to make sure type cannot
    // be set to anything but investment types.
    return (
      !initialNode ||
      initialNode.qcsType === 'UNKNOWN' ||
      !['INVESTMENT', 'PAYMENT'].includes(initialNode.qcsType) ||
      (initialNode.qcsType === 'INVESTMENT' && node.qcsType === 'INVESTMENT') ||
      (initialNode.qcsType === 'PAYMENT' && node.qcsType === 'PAYMENT')
    );
  }
};

export const balanceSignForType = (accountType: AccountType) => {
  let sign;
  switch (accountType) {
    case 'CREDIT':
    case 'OTHER_LIABILITY':
    case 'LOAN':
      sign = -1;
      break;
    default:
      sign = +1;
  }
  return sign;
};

export const isAccountInError = (account?: AccountProps) => {
  let isError = false;
  const aggStatus = account?.aggregators?.get(0)?.aggStatus;

  // TODO: A number of statuses are OK and should not result in an error
  if (aggStatus && aggStatus !== 'OK') {
    isError = true;
  }
  return {
    isError,
    errorStatus: aggStatus,
  };
};

export const hasAggregationError = (account?: AccountProps) => isErrorType(account?.aggregators?.get(0)?.aggStatus);

// An account whose own aggregation status is an error, or that is fully disconnected, can't be
// trusted for live data (e.g. credit limit), and should be shown as "needs attention". Closed
// accounts never need attention: nothing can be done about them.
export const isAccountNeedsAttention = (account?: AccountProps): boolean =>
  !account?.isClosed && (hasAggregationError(account) || Boolean(isAccountDisconnected(account)));

// Shared by AccountEditDialog (web), EditAccount (mobile), and the CreditCardDashboard's AccountCard
// (mobile): a closed, disconnected, or erroring account can't have its credit card fields (limit,
// interest rate) reliably read or written, so none of those surfaces should offer to edit them.
export const canEditCreditCardInformation = (account?: AccountProps) =>
  !account?.isClosed && !hasAggregationError(account) && !isAccountDisconnected(account);

/**
 * Digits of the aggregator's masked account number, e.g. `XXXXXX5854` -> `5854`. Aggregators mask
 * inconsistently (X-padded, bullets, asterisks, bare digits), so everything non-numeric is dropped.
 * Reads the aggregator's copy, not the account-level field.
 */
export const getAccountFinalFourDigits = (account?: AccountProps): string =>
  account?.aggregators
    ?.first()
    ?.accountNumberMasked?.replaceAll?.(/\D/g, '')
    ?.replaceAll?.(/x|X|\*/gi, '') ?? '';

export const formatFinalFour = (account?: AccountProps, useX?: boolean) => {
  const final4Expected = getAccountFinalFourDigits(account) || null;
  const maskingItems = useX ? 'xxxx' : '••••';
  return final4Expected ? `${maskingItems}${final4Expected}` : '';
};

export const getAccountStatementDueAmount = (account?: AccountProps): number | null =>
  typeof account?.statementDueAmount === 'number' ? Math.abs(account.statementDueAmount) : null;

// these are the names we are currently blocking due to amcharts overlap
export const isInvalidName = (accountName: string) => {
  const invalidNames = ['total', 'isoMask', 'custom', 'category', 'txns', 'timeStamp'];
  return invalidNames.includes(accountName);
};

//-------------------------------------------------------------------------------------------------
// Business Related Selectors
//-------------------------------------------------------------------------------------------------

export const doesAccountSupportPaymentSrcs = (account?: AccountProps) =>
  account?.type === 'BANK' ||
  (account?.type === 'OTHER_ASSET' && (account?.subType === 'UNDEPOSITED_FUNDS' || account?.subType === 'UNKNOWN')) ||
  account?.type === 'CASH' ||
  account?.type === 'PAYMENT' ||
  (account?.type === 'OTHER_LIABILITY' && account?.subType === 'UNEARNED_INCOME');

// Asset accounts generally support refund sources. However, some types or subtypes are not applicable. Currently,
// will exclude REAL_ESTATE and VEHICLE accounts but should also exclude some investment subtype accounts.
//
export function doesAccountSupportRefundSrcs(account: AccountProps) {
  return (
    classifyAccountType(account.type) === 'ASSET' &&
    account.type !== 'ACCOUNTS_RECEIVABLE' &&
    account.type !== 'REAL_ESTATE' &&
    account.type !== 'VEHICLE'
  );
}

const SHOW_CHECK_NUMBER_ON_ACCOUNT_SUB_TYPES = new Set([
  // banking
  'CHECKING',
  'SAVINGS',
  'MONEY_MARKET',
  'OTHER_BANK',
  // investment
  'BROKERAGE',
  //payment
  'PAYMENT',
]);

export const hasCheckNumberField = (account?: AccountProps) =>
  !!account?.subType && SHOW_CHECK_NUMBER_ON_ACCOUNT_SUB_TYPES.has(account?.subType);

export function classifyAccountType(accountType: AccountType): AccountClassification {
  const assetTypes: AccountType[] = [
    'BANK',
    'CASH',
    'REAL_ESTATE',
    'VEHICLE',
    'OTHER_ASSET',
    'INVESTMENT',
    'GOAL',
    'ACCOUNTS_RECEIVABLE',
  ];

  const liabilityTypes: AccountType[] = ['CREDIT', 'LOAN', 'ACCOUNTS_PAYABLE', 'OTHER_LIABILITY', 'PAYMENT'];

  // const equityTypes: AccountType[] = [
  //   'RETAINED_INCOME'
  // ];

  if (assetTypes.includes(accountType)) return 'ASSET';
  if (liabilityTypes.includes(accountType)) return 'LIABILITY';
  // if (equityTypes.includes(accountType)) return 'EQUITY';

  return 'UNKNOWN';
}

export const isQBPAccount = (account: AccountProps): boolean =>
  (account.type === 'OTHER_LIABILITY' && account.subType === 'SALES_TAX') ||
  (account.type === 'OTHER_ASSET' && (account.subType === 'UNDEPOSITED_FUNDS' || account.subType === 'UNKNOWN')) ||
  account.type === 'ACCOUNTS_RECEIVABLE' ||
  account.type === 'ACCOUNTS_PAYABLE' ||
  account.type === 'PAYMENT' ||
  isStripeAccount(account);

export const normalizeBusinessIdForUsage = (businessId: string | null | undefined): string | undefined => {
  if (!businessId || businessId === '0' || businessId === 'PERSONAL') {
    return undefined;
  }
  return businessId;
};

// `undefined` means "leave the current businessId alone" (entitlement off); `''` means Personal.
export const getBusinessIdForAccountSelection = ({
  account,
  businessEnabled,
}: {
  account: AccountProps | null | undefined;
  businessEnabled: boolean;
}): string | undefined => {
  if (!businessEnabled) return undefined;
  return account?.businessId || '';
};

export type ResolvedAccountFieldValue = { value: number | undefined; isManual: boolean };

const resolveAggregatorOrManualValue = (
  aggregatedValue: number | undefined,
  manualValue: number | undefined,
): ResolvedAccountFieldValue => {
  if (typeof aggregatedValue === 'number') return { value: aggregatedValue, isManual: false };
  // -1 is the sentinel a manually-entered field uses for "not set"; translate it to undefined here
  // so every consumer of this function can treat "unset" as a single, consistent value.
  return { value: manualValue === -1 ? undefined : manualValue, isManual: true };
};

export const getResolvedAccountCreditLimit = (account?: AccountProps): ResolvedAccountFieldValue =>
  resolveAggregatorOrManualValue(account?.aggregators?.first?.()?.creditLimit, account?.creditLimit);

export const getResolvedAccountInterestRate = (account?: AccountProps): ResolvedAccountFieldValue =>
  resolveAggregatorOrManualValue(account?.aggregators?.first?.()?.interestRate, account?.interestRate);

export const filterAccountsByPredicate = (
  accountsById: ImmutableMap<string, Account> | null | undefined,
  predicate: (account: Account) => boolean,
  emptyDefault: ImmutableMap<string, Account>,
): ImmutableMap<string, Account> => (accountsById == null ? emptyDefault : accountsById.filter(predicate));
